import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { validateCustomerDetails } from '../customerDetails';
import { supabaseClient } from '../supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authMode, setAuthMode] = useState(null);

  useEffect(() => {
    let active = true;

    supabaseClient.auth.getUser().then(({ data }) => {
      if (active) setUser(data.user || null);
    });

    const { data } = supabaseClient.auth.onAuthStateChange((event, session) => {
      setUser(session?.user || null);
    });

    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo(() => ({
    authMode,
    closeAuth: () => setAuthMode(null),
    login: async (email, password) => supabaseClient.auth.signInWithPassword({ email, password }),
    logout: async () => supabaseClient.auth.signOut(),
    openLogin: () => setAuthMode('login'),
    openSignup: () => setAuthMode('signup'),
    signup: async (name, email, phone, password) => {
      const validation = validateCustomerDetails({ name, email, phone });
      const validationError = validation.errors.name || validation.errors.email || validation.errors.phone;

      if (validationError) {
        return { data: null, error: new Error(validationError) };
      }

      return supabaseClient.auth.signUp({
        email: validation.normalized.email,
        password,
        options: {
          data: {
            full_name: validation.normalized.name,
            phone: validation.normalized.phone,
          },
        },
      });
    },
    user,
  }), [authMode, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
